import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PageHeader,
  DashboardCard,
  FilterBar,
  StatusBadge,
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@zafroi/ui-kit';
import { useContracts } from '@/hooks/useContracts';
import { useCustomerNameMap } from '@/hooks/useKosh';
import { QueryStateGuard } from '@/components/common/QueryStateGuard';
import { contractStatusVariants } from '@/config/contract-status';
import { formatMoney, shortDate } from '@/lib/format';

export function ContractsPage() {
  const navigate = useNavigate();
  const q = useContracts();
  const nameMap = useCustomerNameMap();
  const [search, setSearch] = useState('');

  const contracts = q.data ?? [];
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return contracts;
    return contracts.filter(
      (c) =>
        c.contractNumber.toLowerCase().includes(s) ||
        (nameMap.get(c.customerId) ?? '').toLowerCase().includes(s),
    );
  }, [contracts, search, nameMap]);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Commercial"
        title="Contracts"
        description="Service agreements — activate them and bill their milestones."
      />
      <DashboardCard flushBody>
        <div className="border-b border-border p-3">
          <FilterBar
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search contracts…"
          />
        </div>
        <QueryStateGuard
          data={filtered}
          isLoading={q.isLoading}
          isError={q.isError}
          emptyTitle="No contracts yet"
          emptyDescription="Sell a professional-service offering to create one."
        >
          {(rowsData) => (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Number</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Value</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rowsData.map((c) => (
                  <TableRow
                    key={c.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/contracts/${c.id}`)}
                  >
                    <TableCell className="font-mono text-[12px]">{c.contractNumber}</TableCell>
                    <TableCell className="font-medium">
                      {nameMap.get(c.customerId) ?? c.customerId.slice(0, 8)}
                    </TableCell>
                    <TableCell>{c.contractType}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatMoney(c.value, c.currency)}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={c.status} variants={contractStatusVariants} />
                    </TableCell>
                    <TableCell className="text-muted-foreground">{shortDate(c.createdAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </QueryStateGuard>
      </DashboardCard>
    </div>
  );
}
